import { Box } from "@rebass/grid";
import * as authActions from "@rentiohq/shared-frontend/dist/redux/auth/auth.actions";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as authUtils from "@rentiohq/shared-frontend/dist/redux/auth/auth.utils";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { appendQueryParams } from "@rentiohq/shared-frontend/dist/utils/url.utils";
import {
  IOptionListOption,
  Loading,
  Modal,
  AsyncSelect,
  TextStyle,
  Select,
} from "@rentiohq/web-shared/dist/components";
import EnabledAccountsSelectForceRefresh from "components/EnabledAccountsSelectForceRefresh";
import React from "react";
import { useDispatch } from "react-redux";
import {
  getDisplayName,
  sortOptionsByInput,
} from "./EnabledAccountsSelect.utils";

const QUERY_DELAY = 500;
const QUERY_SEARCH_LIMIT = 2;

export interface IEnabledAccount {
  firstname?: string;
  lastname?: string;
  company?: string;
  id: number;
  userId: number;
  baseAccountId?: number;
}

interface IProps {
  showModal: boolean;
  onClose: () => void;
}

const EnabledAccountsSelect = (props: IProps) => {
  const { showModal, onClose } = props;

  // Redux
  const dispatch = useDispatch();
  const { user, isMonitor } = authHooks.useSelf();
  // State
  const [selectedAccountId, setSelectedAccountId] = React.useState<
    number | undefined
  >();
  const [input, setInput] = React.useState("");
  const [enabledAccounts, setEnabledAccounts] = React.useState<
    IEnabledAccount[]
  >([]);
  const [isFetching, setIsFetching] = React.useState(false);
  const timerRef = React.useRef<number | null>(null);

  const fetchAccounts = async (
    rawQuery?: string,
  ): Promise<IEnabledAccount[] | undefined> => {
    if (!showModal || !user) return;

    const trimmed = rawQuery?.trim();

    const searchQuery =
      trimmed && trimmed.length >= QUERY_SEARCH_LIMIT ? trimmed : undefined;

    if (isMonitor && !searchQuery) return;

    try {
      setIsFetching(true);
      const path = appendQueryParams({
        path: `/users/${user.id}/enabled-accounts`,
        queryParams: {
          search: searchQuery,
        },
      });
      const response = await api.get<IEnabledAccount[]>(path);

      const accounts = response.data;

      if (!isMonitor) {
        setEnabledAccounts(accounts);
        setIsFetching(false);
        return;
      }

      setIsFetching(false);
      return accounts;
    } catch (err) {
      setIsFetching(false);
      return undefined;
    }
  };

  const handleLoadOptions = (
    input: string,
    callback: (opts: any[]) => void,
  ) => {
    if (!user) {
      callback([]);
      return;
    }

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = window.setTimeout(async () => {
      const accounts = await fetchAccounts(input);

      const options = (accounts || []).map(item => ({
        label: getDisplayName(item, user),
        value: item.id,
      }));
      callback(options);
      timerRef.current = null;
    }, QUERY_DELAY);
  };

  const handleSelectAccount = (option: IOptionListOption | null) => {
    if (!option) {
      setSelectedAccountId(undefined);
      return;
    }

    if (typeof option.value !== "number") return;
    setSelectedAccountId(option.value);
  };

  const handleUseSelectedAccount = async () => {
    if (!user) {
      return;
    }

    const accountId = selectedAccountId || user.activeAccountId;
    if (!accountId) {
      return;
    }

    try {
      const newAccessTokenObject = await api.post<{
        id: /* accessToken */ string;
        refreshToken: string;
      }>(`/users/${user.id}/enabled-accounts/${accountId}/use`);

      onClose();

      dispatch(
        authActions.setAccessTokensWithoutSideEffects({
          accessToken: newAccessTokenObject.data.id,
          refreshToken: newAccessTokenObject.data.refreshToken,
        }),
      );

      await authUtils.setAccessTokenLastAccountSelectDate(new Date());

      if (isMonitor) {
        window.location.href = `${window.location.origin}/monitor`;
      } else {
        window.location.href = window.location.origin;
      }
    } catch (unknownError) {
      const error = unknownError as any;
      showAlert({
        type: "error",
        error,
      });
    }
  };

  React.useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (showModal && !isMonitor) {
      fetchAccounts();
    }
  }, [showModal, isMonitor]);

  if (!user) {
    return null;
  }

  return (
    <>
      {showModal && (
        <Modal
          width="medium"
          shouldCloseOnOverlayClick={true}
          hasDismiss={true}
          onClose={onClose}
          heading={getLocalizedText(
            "modal.enabled_accounts_select.select.title",
          )}
          actions={
            !!selectedAccountId
              ? [
                  {
                    content: getLocalizedText("system.switch"),
                    onClick: handleUseSelectedAccount,
                    appearance: "primary",
                  },
                ]
              : []
          }
        >
          {isMonitor ? (
            <AsyncSelect
              cacheOptions
              id="user-select"
              label={getLocalizedText(
                "modal.enabled_accounts_select.select.dropdown_label",
              )}
              value={selectedAccountId || user?.activeAccountId}
              onChange={handleSelectAccount}
              loadOptions={handleLoadOptions}
              blurInputOnSelect
              inputValue={undefined}
              placeholder={getLocalizedText(
                "modal.enabled_accounts_select.select.dropdown_label",
              )}
            />
          ) : (
            <Select
              id={"user-select"}
              label={getLocalizedText(
                "modal.enabled_accounts_select.select.dropdown_label",
              )}
              onInputChange={(value: string) => setInput(value)}
              value={selectedAccountId || user.activeAccountId}
              onChange={handleSelectAccount}
              options={sortOptionsByInput(
                enabledAccounts?.map(item => ({
                  label: getDisplayName(item, user),
                  value: item.id,
                })),
                input,
              )}
            />
          )}
          {isFetching && <Loading />}

          <Box mt={1}>
            <TextStyle variation="subdued">
              {getLocalizedText("enabled_accounts.search_restriction.message")}
            </TextStyle>
          </Box>
        </Modal>
      )}
      <EnabledAccountsSelectForceRefresh />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default EnabledAccountsSelect;
